import { STORYBLOK_ROUTES } from '#shared/constants/storyblok';
import type { Breadcrumb } from '~/types/breadcrumb.type';

export type UseStoryblokBlogBreadcrumbsReturn = ComputedRef<Breadcrumb[]>;

export const useStoryblokBlogBreadcrumbs = async (
    fullSlug: MaybeRefOrGetter<string | undefined>,
    currentLabel: string
): Promise<UseStoryblokBlogBreadcrumbsReturn> => {
    const storyblokApi = useStoryblokApi();
    const { tt, locale, isDefaultLocale } = useTypedI18n();

    const key = computed(() => `breadcrumbs-${toValue(fullSlug) ?? '_'}-${locale.value}`);

    const { data: stories } = await useAsyncData(key, async () => {
        const slugValue = toValue(fullSlug);

        if (!slugValue) return [];

        const segments = slugValue.split('/').filter(Boolean);
        const parentSlugs = segments.slice(0, -1).map((_, i) => `${segments.slice(0, i + 1).join('/')}/`);

        if (parentSlugs.length === 0) return [];

        return (
            await storyblokApi.getStories({
                by_slugs: parentSlugs.join(','),
                version: 'published',
                ...(!isDefaultLocale.value ? { language: locale.value } : {}),
            })
        ).data.stories;
    });

    const sortedParentStories = computed(() =>
        [...(stories.value ?? [])]
            .sort((a, b) => a.full_slug.split('/').filter(Boolean).length - b.full_slug.split('/').filter(Boolean).length)
            .map(s => ({ label: s.content.title, to: `/${s.full_slug.replace(new RegExp(`^${locale.value}/`), '')}` }))
    );

    return computed<Breadcrumb[]>(() => {
        const slugValue = toValue(fullSlug);

        return [
            {
                label: tt('homepage.link'),
                to: STORYBLOK_ROUTES.HOMEPAGE,
            },
            ...sortedParentStories.value,
            ...(slugValue ? [{ label: currentLabel, to: `/${slugValue}` }] : []),
        ];
    });
};
